import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import InfiniteScrollSentinel from "../components/InfiniteScrollSentinel";
import ProductListSearchBar from "../components/ProductListSearchBar";
import CatalogFilterButton from "../components/CatalogFilterButton";
import ListFetchError from "../components/ListFetchError";
import BackButton from "../components/BackButton";
import { normalizeStoreKey, PLATFORM_LABELS } from "../utils/catalogFilters";
import {
  applyCatalogListFilters,
  applyCatalogSearchQuery,
  clearCatalogListFilters,
  getCatalogFilterParamsKey,
  joinCatalogFilterValues,
  readCatalogListFilters,
} from "../utils/catalogTabState";
import { useResetCatalogFiltersOnQueryChange } from "../hooks/useResetCatalogFiltersOnQueryChange";
import { useResetCatalogFiltersOnRouteEntry } from "../hooks/useResetCatalogFiltersOnRouteEntry";
import {
  toggleCatalogCategorySelection,
} from "../utils/catalogListFilterOptions";
import { useStableCatalogFilterOptions } from "../hooks/useStableCatalogFilterOptions";
import {
  getInfiniteScrollNextRequest,
  hasMoreInfiniteItems,
  resetInfiniteScrollToTop,
} from "../utils/infiniteScrollPaging";
import useProductListInfiniteScroll from "../hooks/useProductListInfiniteScroll";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { fetchActowizTopDeals } from "../store/actowiz/actowizSlice";

const BRAND_OPTIONS = [
  { key: "tesco", label: "Tesco" },
  { key: "asda", label: "Asda" },
  { key: "morrison", label: "Morrisons" },
  { key: "sainsbury", label: "Sainsbury's" },
];

function TopDealsProducts({ onOpenAuth, onOpenMenu }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const topDeals = useAppSelector((s) => s.actowiz.topDeals);
  const topDealsLastBatchSize = useAppSelector((s) => s.actowiz.topDealsLastBatchSize);
  const topDealsFetchedTotal = useAppSelector((s) => s.actowiz.topDealsFetchedTotal);
  const status = useAppSelector((s) => s.actowiz.topDealsStatus);
  const error = useAppSelector((s) => s.actowiz.topDealsError);
  const topDealsTotal = useAppSelector((s) => s.actowiz.topDealsTotal);
  const topDealsCatalog = useAppSelector((s) => s.actowiz.topDealsCatalog);

  const appliedQuery = (searchParams.get("q") || "").trim();
  const queryListKey = useMemo(() => appliedQuery.toLowerCase(), [appliedQuery]);
  const filterParamsKey = useMemo(
    () => getCatalogFilterParamsKey(searchParams, { includeBrands: true }),
    [searchParams]
  );
  const { brands: selectedBrands, categories: selectedCategories, subcategories: selectedSubcategories } =
    useMemo(
      () => readCatalogListFilters(searchParams, { includeBrands: true }),
      [filterParamsKey, searchParams]
    );

  useResetCatalogFiltersOnQueryChange({
    searchParams,
    queryValue: appliedQuery,
    onApply: (next) => setSearchParams(next, { replace: true }),
    includeBrands: true,
  });

  useResetCatalogFiltersOnRouteEntry({
    searchParams,
    onApply: (next) => setSearchParams(next, { replace: true }),
    includeBrands: true,
  });

  const catalogForFilters = useMemo(() => {
    const catalogQueryKey = String(topDealsCatalog?.queryKey ?? "").trim().toLowerCase();
    if (
      catalogQueryKey === queryListKey &&
      Array.isArray(topDealsCatalog?.categories) &&
      topDealsCatalog.categories.length > 0
    ) {
      return topDealsCatalog;
    }
    return null;
  }, [topDealsCatalog, queryListKey]);

  const filterOptionsSearchKey = useMemo(
    () => `top-deals:${queryListKey}`,
    [queryListKey]
  );

  const prevFilterKeyRef = useRef("");
  const loadingMoreRef = useRef(false);

  const [brandsOpen, setBrandsOpen] = useState(true);
  const [categoryOpen, setCategoryOpen] = useState(true);
  const [subCategoryOpen, setSubCategoryOpen] = useState(true);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [searchText, setSearchText] = useState(appliedQuery);
  const [noResultModalOpen, setNoResultModalOpen] = useState(false);
  const [dismissedNoResultQuery, setDismissedNoResultQuery] = useState("");

  const filterKey = useMemo(
    () =>
      [
        appliedQuery,
        joinCatalogFilterValues(selectedBrands),
        joinCatalogFilterValues(selectedCategories),
        joinCatalogFilterValues(selectedSubcategories),
      ].join("|"),
    [appliedQuery, selectedBrands, selectedCategories, selectedSubcategories]
  );

  const fetchTopDeals = useCallback(
    ({ apiLoadedCount = 0, isRefresh = false } = {}) => {
      const { start, offset, mode } = getInfiniteScrollNextRequest({ apiLoadedCount, isRefresh });
      dispatch(
        fetchActowizTopDeals({
          query_list: appliedQuery || undefined,
          platform_list: selectedBrands.size ? Array.from(selectedBrands) : undefined,
          category: selectedCategories.size ? Array.from(selectedCategories) : undefined,
          sub_category: selectedSubcategories.size ? Array.from(selectedSubcategories) : undefined,
          start,
          offset,
          mode,
          force: true,
        })
      );
    },
    [dispatch, appliedQuery, selectedBrands, selectedCategories, selectedSubcategories]
  );

  const replaceListParams = useCallback(
    (patch, { q } = {}) => {
      const next = applyCatalogListFilters(searchParams, patch);
      if (q !== undefined) {
        const trimmed = String(q || "").trim();
        if (trimmed) next.set("q", trimmed);
        else next.delete("q");
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const displayItems = useMemo(() => (topDeals || []).filter((p) => p?.title), [topDeals]);

  const catalogTotal =
    topDealsCatalog?.queryKey === queryListKey &&
    typeof topDealsCatalog.totalCount === "number"
      ? topDealsCatalog.totalCount
      : null;
  const totalProductsCount =
    typeof topDealsTotal === "number" && topDealsTotal >= 0
      ? topDealsTotal
      : typeof catalogTotal === "number" && catalogTotal >= 0
      ? catalogTotal
      : displayItems.length;

  useEffect(() => {
    setSearchText(appliedQuery);
  }, [appliedQuery]);

  useEffect(() => {
    if (prevFilterKeyRef.current === filterKey) return;
    prevFilterKeyRef.current = filterKey;
    loadingMoreRef.current = false;
    resetInfiniteScrollToTop();
    fetchTopDeals({ isRefresh: true });
  }, [filterKey, fetchTopDeals]);

  useEffect(() => {
    if (status !== "succeeded") {
      if (status === "loading" || status === "idle") setNoResultModalOpen(false);
      return;
    }
    const queryKey = String(appliedQuery || "").trim();
    if (!queryKey) {
      setNoResultModalOpen(false);
      setDismissedNoResultQuery("");
      return;
    }
    if (error) {
      setNoResultModalOpen(false);
      return;
    }
    if (displayItems.length > 0) {
      setNoResultModalOpen(false);
      setDismissedNoResultQuery("");
      return;
    }
    setNoResultModalOpen(queryKey !== dismissedNoResultQuery);
  }, [status, appliedQuery, displayItems.length, error, dismissedNoResultQuery]);

  const rawItemsCount = (topDeals || []).length;
  const isInitialLoading = status === "loading" && rawItemsCount === 0;
  const isLoadingMore = status === "loading" && rawItemsCount > 0;
  const isLoadMoreError = Boolean(error) && displayItems.length > 0;
  const hasMore = hasMoreInfiniteItems({ lastBatchSize: topDealsLastBatchSize });

  const loadMore = useCallback(() => {
    if (status === "loading" || !hasMore || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    fetchTopDeals({ apiLoadedCount: topDealsFetchedTotal, isRefresh: false });
  }, [status, hasMore, topDealsFetchedTotal, fetchTopDeals]);

  const retryFetch = useCallback(() => {
    if (status === "loading") return;
    loadingMoreRef.current = false;
    if (displayItems.length === 0) {
      fetchTopDeals({ isRefresh: true });
      return;
    }
    fetchTopDeals({ apiLoadedCount: topDealsFetchedTotal, isRefresh: false });
  }, [status, displayItems.length, topDealsFetchedTotal, fetchTopDeals]);

  useEffect(() => {
    if (status !== "loading") {
      loadingMoreRef.current = false;
    }
  }, [status]);

  const { bindPrefetchNode, prefetchIndex } = useProductListInfiniteScroll({
    itemCount: displayItems.length,
    hasMore: hasMore && !error,
    isLoading: status === "loading",
    onLoadMore: loadMore,
  });

  const applySearch = () => {
    const next = String(searchText || "").trim();
    if (!next || next === appliedQuery) return;
    setDismissedNoResultQuery("");
    setNoResultModalOpen(false);
    resetInfiniteScrollToTop();
    setSearchParams(applyCatalogSearchQuery(searchParams, next, { includeBrands: true }), {
      replace: true,
    });
  };

  const resetFilters = () => {
    setSearchParams(clearCatalogListFilters(searchParams, { includeBrands: true }), {
      replace: true,
    });
  };

  const { categoryOptions, subcategoryOptions } = useStableCatalogFilterOptions(
    catalogForFilters,
    filterOptionsSearchKey,
    selectedCategories
  );

  const openProduct = (product) => {
    const productId = product?.product_id ?? product?.pid ?? product?.sku ?? "";
    if (!productId) return;
    const platformKey = normalizeStoreKey(product?.platform) || "tesco";
    navigate(`/product-store/${encodeURIComponent(platformKey)}/${encodeURIComponent(productId)}`, {
      state: {
        product,
        pricingMode: "Loyalty+Offer",
        offer: true,
        fromTopDeals: true,
      },
    });
  };

  const activeFilterCount =
    selectedBrands.size + selectedCategories.size + selectedSubcategories.size;

  const renderFilters = (compact = false) => (
    <>
      <div className={compact ? "border-b border-[#E6EAF0] pb-2" : "bg-white border-b-2 border-[#E6EAF0] pb-2"}>
        <button
          type="button"
          className="flex w-full items-center justify-between px-1 py-2 text-left"
          onClick={() => setBrandsOpen((v) => !v)}
        >
          <span
            className={`rounded-full bg-[#7352C7] px-3 py-1 font-semibold text-white ${
              compact ? "text-[12px]" : "text-[14px]"
            }`}
          >
            Brands
          </span>
          <span className="text-[#6E7B86]">{brandsOpen ? "▴" : "▾"}</span>
        </button>
        {brandsOpen && (
          <div
            className={`space-y-2 px-3 pb-3 text-[#6E7B86] ${compact ? "px-2 pb-2 text-[14px]" : "text-[16px]"}`}
          >
            {BRAND_OPTIONS.map((b) => (
              <label key={b.key} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedBrands.has(b.key)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    const next = new Set(selectedBrands);
                    if (checked) next.add(b.key);
                    else next.delete(b.key);
                    replaceListParams({ brands: next });
                  }}
                  className="h-3.5 w-3.5 accent-[#7352C7]"
                  style={{ accentColor: "#7352C7" }}
                />
                <span>{b.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className={compact ? "border-b border-[#E6EAF0] pb-2" : "bg-white border-b-2 border-[#E6EAF0] pb-2"}>
        <button
          type="button"
          className="flex w-full items-center justify-between px-1 py-2 text-left"
          onClick={() => setCategoryOpen((v) => !v)}
        >
          <span
            className={`rounded-full bg-[#7352C7] px-3 py-1 font-semibold text-white ${
              compact ? "text-[12px]" : "text-[14px]"
            }`}
          >
            Category
          </span>
          <span className="text-[#6E7B86]">{categoryOpen ? "▴" : "▾"}</span>
        </button>
        {categoryOpen && (
          <div
            className={`space-y-2 px-3 pb-3 text-[#6E7B86] ${compact ? "px-2 pb-2 text-[14px]" : "text-[16px]"}`}
          >
            {categoryOptions.length === 0 && (
              <div className="text-[12px] text-slate-400">No categories</div>
            )}
            {categoryOptions.map((c) => (
              <label key={c} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedCategories.has(c)}
                  onChange={(e) => {
                    const { categories, subcategories } = toggleCatalogCategorySelection({
                      selectedCategories,
                      selectedSubcategories,
                      category: c,
                      checked: e.target.checked,
                      catalog: catalogForFilters,
                    });
                    replaceListParams({
                      categories,
                      subcategories,
                    });
                  }}
                  className="h-3.5 w-3.5 accent-[#7352C7]"
                  style={{ accentColor: "#7352C7" }}
                />
                <span>{c}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className={compact ? "pb-2" : "bg-white pb-2"}>
        <button
          type="button"
          className="flex w-full items-center justify-between px-1 py-2 text-left"
          onClick={() => setSubCategoryOpen((v) => !v)}
        >
          <span
            className={`rounded-full bg-[#7352C7] px-3 py-1 font-semibold text-white ${
              compact ? "text-[12px]" : "text-[14px]"
            }`}
          >
            Sub Category
          </span>
          <span className="text-[#6E7B86]">{subCategoryOpen ? "▴" : "▾"}</span>
        </button>
        {subCategoryOpen && (
          <div
            className={`space-y-2 px-3 pb-3 text-[#6E7B86] ${compact ? "px-2 pb-2 text-[14px]" : "text-[16px]"}`}
          >
            {subcategoryOptions.length === 0 && (
              <div className="text-[12px] text-slate-400">No sub categories</div>
            )}
            {subcategoryOptions.map((sc) => (
              <label key={sc} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedSubcategories.has(sc)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    const next = new Set(selectedSubcategories);
                    if (checked) next.add(sc);
                    else next.delete(sc);
                    replaceListParams({ subcategories: next });
                  }}
                  className="h-3.5 w-3.5 accent-[#7352C7]"
                  style={{ accentColor: "#7352C7" }}
                />
                <span>{sc}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#24303A] flex flex-col">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="w-full flex-1 px-3 py-4 sm:px-4 sm:py-6 md:px-6">
        <div className="flex min-w-0 gap-4 md:gap-6">
          <aside className="sticky top-24 hidden h-[calc(100vh-90px)] w-64 shrink-0 rounded-2xl border border-[#E6EAF0] bg-white p-4 text-[16px] shadow-sm md:block">
            <div className="mb-4 flex items-center justify-end border-b-2 border-[#E6EAF0] pb-4">
              <button
                type="button"
                onClick={resetFilters}
                className="rounded-full bg-[#7352C7] px-3 py-1 text-[16px] font-semibold text-white shadow-sm"
              >
                Reset
              </button>
            </div>
            <div className="space-y-4 overflow-y-auto pr-2 h-[calc(100%-56px)]">{renderFilters()}</div>
          </aside>

          <div className="min-w-0 flex-1 space-y-4 sm:space-y-6">
            <div className="flex min-w-0 flex-col gap-3 rounded-2xl border border-[#E6EAF0] bg-white p-4 shadow-sm sm:gap-4 sm:p-5 lg:flex-row lg:items-end lg:justify-between lg:gap-6">
              <div className="min-w-0 flex-1">
                <BackButton className="mb-2 sm:mb-3" />
                <h1 className="text-[22px] font-bold leading-tight text-[#24303A] sm:text-[24px]">
                  Today&apos;s Top Deals
                </h1>
                <p className="mt-1 text-[14px] text-[#6E7B86] sm:text-[16px]">
                  Showing {displayItems.length} of {totalProductsCount} products
                </p>
              </div>
              <ProductListSearchBar
                value={searchText}
                onChange={setSearchText}
                onSearch={applySearch}
                placeholder="Search top deals"
              />
            </div>

            <div className="flex md:hidden">
              <CatalogFilterButton
                count={activeFilterCount}
                onClick={() => setMobileFiltersOpen(true)}
                className="ml-auto"
              />
            </div>

            {isInitialLoading && !error && (
              <div className="flex justify-center py-16">
                <span className="text-[16px] text-slate-500">Loading…</span>
              </div>
            )}

            {(error || status === "failed") && !isLoadMoreError && (
              <ListFetchError message={error} onRetry={retryFetch} isLoading={status === "loading"} />
            )}

            {!error && status !== "loading" && displayItems.length === 0 && !appliedQuery && (
              <div className="flex justify-center py-16">
                <span className="text-[16px] text-slate-500">No deals found.</span>
              </div>
            )}

            {displayItems.length > 0 && (
              <div
                className={`grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3${
                  isLoadingMore ? "" : status === "loading" ? " pointer-events-none opacity-60" : ""
                }`}
              >
                {displayItems.map((product, idx) => {
                  const label =
                    PLATFORM_LABELS[normalizeStoreKey(product?.platform)] || "Store";
                  return (
                    <div
                      key={`${product.pid || product.sku}-${idx}`}
                      ref={idx === prefetchIndex ? bindPrefetchNode : undefined}
                      className="min-w-0"
                    >
                      <ProductCard
                        accent={["orange", "blue", "green", "purple"][idx % 4]}
                        product={product}
                        showComparison={false}
                        hideAddToList
                        retailerLabel={label}
                        pricingMode="Loyalty+Offer"
                        onClick={() => openProduct(product)}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {(hasMore || isLoadingMore) && !isLoadMoreError && (
              <InfiniteScrollSentinel isLoading={isLoadingMore} hasMore={hasMore} />
            )}

            {isLoadMoreError && (
              <ListFetchError message={error} onRetry={retryFetch} isLoading={status === "loading"} />
            )}
          </div>
        </div>
      </main>

      <Footer />

      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-[80] md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close filters"
            onClick={() => setMobileFiltersOpen(false)}
          />
          <div className="absolute right-0 top-0 flex h-full max-h-[100dvh] w-[88%] max-w-sm flex-col border-l border-[#E6EAF0] bg-white shadow-xl">
            <div className="flex shrink-0 items-center justify-end border-b border-[#E6EAF0] p-4 pb-3">
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(false)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#E6EAF0] text-slate-600"
                aria-label="Close filters panel"
              >
                ✕
              </button>
            </div>
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 pb-2 pt-2">
              {renderFilters(true)}
            </div>
            <div className="shrink-0 border-t border-[#E6EAF0] bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    resetFilters();
                    setMobileFiltersOpen(false);
                  }}
                  className="w-1/2 min-w-0 rounded-full border border-[#E6EAF0] bg-white px-3 py-1 text-[16px] font-semibold text-[#24303A]"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(false)}
                  className="w-1/2 min-w-0 rounded-full bg-[#7352C7] px-3 py-2 text-[14px] font-semibold text-white"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {noResultModalOpen && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white p-6 text-center shadow-xl">
            <h3 className="text-[24px] font-bold text-[#24303A]">Oops</h3>
            <p className="mt-2 text-[16px] text-[#6E7B86]">
              No matching result found try to change the name and try again
            </p>
            <button
              type="button"
              onClick={() => {
                setNoResultModalOpen(false);
                setDismissedNoResultQuery(String(appliedQuery || "").trim());
              }}
              className="mt-5 rounded-full bg-[#FF3C3F] px-8 py-2 text-[16px] font-semibold text-white shadow-sm"
            >
              Try Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TopDealsProducts;
